import { useNotification } from "@expert/common-ui";
import { useFeatures, type WorkspaceFeatures } from "@expert/features";
import { useClipboard, useDisclosure } from "@mantine/hooks";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTimer } from "react-timer-hook";

import { usePartnerConfig } from "@/partner/usePartnerConfig";
import { isVoiceTask, useAgentSdk, useAgentStore, useCallbacksRemaining, usePartner, useSession } from "@/sdk";
import { useLogger } from "@/sdk/logging/LoggerProvider";
import { useCallbackStore } from "@/sdk/sessions/callback.store";

import type { SessionSummaryView, SessionSummaryViewProps } from "./sessionSummaryView";

import { useSessionSummaryStore } from "../store/useSessionSummaryStore";
import { getPartnerTool } from "../utils";
import { useIsCustomerDroppedNoticeEnabled } from "./customerDroppedNotice";
import { useSummary } from "./useSummary";

/** Wrap-up behavior shared by the standard and dynamic session summary layouts. */
export function useSessionSummaryViewModel(): SessionSummaryViewProps {
    const logger = useLogger({ module: "NewSessionSummary", supportTeam: "solve" });
    const clipboard = useClipboard({ timeout: 500 });
    const agentSdk = useAgentSdk();
    const partner = usePartner();
    const notification = useNotification();
    const { wrappingAgentActivity } = usePartnerConfig();

    const [view, setView] = useState<SessionSummaryView>("edit-summary");
    const [summaryLoaded, setSummaryLoaded] = useState(false);
    const [summaryGenerationTimedOut, setSummaryGenerationTimedOut] = useState(false);
    const [isCopied, setIsCopied] = useState<boolean>(false);
    const {
        features: { isAutoSummaryEnabled },
    } = useFeatures<WorkspaceFeatures>();
    const { gaiaSummaryModel } = usePartnerConfig();
    const isCustomerDroppedNoticeEnabled = useIsCustomerDroppedNoticeEnabled();
    const [opened, { open: openModal, close: closeModal }] = useDisclosure(false);

    const customerEndedCall = useSessionSummaryStore((state) => state.customerEndedCall);
    const showCallEndedBanner = customerEndedCall && isCustomerDroppedNoticeEnabled;

    const activity = useAgentStore((state) => state.activity);
    const pendingActivity = useAgentStore((state) => state.pendingActivity);
    const activeSession = useSession();
    const activeTask = isVoiceTask(activeSession.currentTask) ? activeSession.currentTask : null;

    const agentCallSid =
        activeTask?.agentCallId ??
        Array.from(activeSession.tasks.values())
            .filter(isVoiceTask)
            .findLast((task) => task.agentCallId !== undefined)?.agentCallId;

    const {
        summaryGenerated,
        validAccountNumber,
        requiresManualCopy,
        sessionSummaryTimes,
        summary,
        fetchAndDispatchSessionSummary,
        isSimplrSummaryFlagsLoading,
        onDismiss,
        getExtraSummaryTime,
    } = useSummary();

    const shouldAutoGenerateSummary = activeTask?.locale !== "es-mx";
    const shouldSendSummaryToPartnerTool = validAccountNumber && (!requiresManualCopy || isAutoSummaryEnabled);
    const needsCopy = !shouldSendSummaryToPartnerTool && !isCopied;
    const sessionSummaryLoading = !summaryLoaded && shouldAutoGenerateSummary && !!gaiaSummaryModel;
    const canSendSuccessfulSummary = summaryGenerated && shouldSendSummaryToPartnerTool;
    const shouldShowTextArea = !sessionSummaryLoading && !isCopied && !canSendSuccessfulSummary;
    const shouldDisableTextArea = summaryGenerated && needsCopy;

    const hasActiveCustomerSessionWrappingState =
        activeSession.kind === "with-customer" && !!activeSession.wrappingState;
    // A wrapping task or a newly wrapping agent means we are entering session summary
    const inWrappingState =
        activeTask?.status === "wrapping" ||
        activity === wrappingAgentActivity ||
        hasActiveCustomerSessionWrappingState;
    const taskIsActive = activeTask?.status === "assigned";
    const { callbackState } = useCallbackStore();
    const { canCallback, callbacksRemaining } = useCallbacksRemaining();
    const hasImmediateCallBack = callbackState?.callbackType === "CallbackNow";

    const DEFAULT_WRAPUP_DURATION = 30000;
    const expirationTimestamp = activeSession.wrappingState?.expirationTimestamp;
    const [fallbackExpiry] = useState(() => Date.now() + DEFAULT_WRAPUP_DURATION);
    const baseExpiry = expirationTimestamp ?? fallbackExpiry;
    const initialTimer = shouldAutoGenerateSummary ? baseExpiry : baseExpiry + sessionSummaryTimes.maximum;

    const endSession = useCallback(
        async (reason: "AgentTerminatedWrapping" | "WrappingTimeExpired") => {
            logger.trace({ reason, partner }, "SessionSummary | Ending session wrapup");
            await agentSdk.endWrapup(reason, partner, activeSession.id, activeTask ?? undefined);
        },
        [activeTask, activeSession.id, agentSdk, logger, partner],
    );

    async function handleWrappingExpired() {
        if (needsCopy) copySummary();
        await endSession("WrappingTimeExpired");
    }

    const copySummary = () => {
        try {
            clipboard.copy(summary);
            setIsCopied(true);
            notification.success("Session summary copied to clipboard");
        } catch (error) {
            logger.warn({ error }, "SessionSummary | Error copying session summary");
        }
    };

    async function handleCopyOrEndSessionClick() {
        if (needsCopy) copySummary();
        else await endSession("AgentTerminatedWrapping");
    }

    const callbackScheduleHandler = async () => await onDismiss(closeModal, agentCallSid, { checkSummary: false });

    function getSummaryHeader() {
        if (summaryGenerationTimedOut) {
            if (requiresManualCopy) {
                if (!summaryGenerated) {
                    return "Oh no—we couldn’t generate a summary";
                }
                return `Add notes to ${getPartnerTool(partner)}`;
            }
            if (!validAccountNumber) {
                return `Autosend failed—add notes to ${getPartnerTool(partner)}`;
            } else if (!summaryGenerated) {
                return "Oh no—we couldn’t generate a summary";
            }
        }
        return "Wrap up";
    }

    function getSummaryButtonText() {
        if (summaryGenerationTimedOut) {
            if (needsCopy) {
                return "Copy notes";
            } else if (pendingActivity === agentSdk.offlineAgentActivity) {
                return "End session and log off";
            } else if (!summaryGenerated && shouldSendSummaryToPartnerTool) {
                return "Save and end session";
            }
            return "End session";
        }
        return "Generating";
    }

    const handleWrappingExpiredRef = useRef(handleWrappingExpired);
    useEffect(() => {
        handleWrappingExpiredRef.current = handleWrappingExpired;
    });

    const { totalSeconds, restart } = useTimer({
        expiryTimestamp: new Date(initialTimer),
        autoStart: true,
        onExpire: () => {
            if (summaryGenerationTimedOut || !shouldAutoGenerateSummary) {
                void handleWrappingExpiredRef.current();
            } else {
                restartSummaryTimer();
            }
        },
    });

    const restartSummaryTimer = useCallback(() => {
        const timestamp = baseExpiry + getExtraSummaryTime(canSendSuccessfulSummary, shouldDisableTextArea);
        setSummaryGenerationTimedOut(true);
        setTimeout(() => {
            restart(new Date(timestamp), true);
        }, 50);
    }, [baseExpiry, canSendSuccessfulSummary, getExtraSummaryTime, restart, shouldDisableTextArea]);

    const appliedExpiryRef = useRef<number | undefined>(undefined);
    useEffect(() => {
        if (!expirationTimestamp || appliedExpiryRef.current === expirationTimestamp) return;
        appliedExpiryRef.current = expirationTimestamp;
        restart(new Date(initialTimer), true);
    }, [expirationTimestamp, initialTimer, restart]);

    useEffect(() => {
        if (summaryLoaded && shouldAutoGenerateSummary) {
            restartSummaryTimer();
        }
    }, [restartSummaryTimer, shouldAutoGenerateSummary, summaryLoaded]);

    useEffect(() => setView("edit-summary"), [opened]);

    const summaryFetchedForCallSidRef = useRef<string | undefined>(undefined);

    useEffect(() => {
        if (!shouldAutoGenerateSummary) {
            setSummaryLoaded(true);
            setSummaryGenerationTimedOut(true);
            return;
        }

        // Fetch the session summary once per wrap-up (keyed on agentCallSid, since this component
        // stays mounted across calls), as soon as we're opened AND the Simplr routing flags have
        // resolved. Firing before they resolve would read their loading-state defaults and
        // silently pin summarySource to "gaia" regardless of the flags' real value — this can
        // happen on the session's first wrap-up (or the first one for a newly-encountered
        // partner), since the flags haven't been fetched for that partner yet at that point.
        if (opened && !isSimplrSummaryFlagsLoading && summaryFetchedForCallSidRef.current !== agentCallSid) {
            summaryFetchedForCallSidRef.current = agentCallSid;
            void fetchAndDispatchSessionSummary(setSummaryLoaded, agentCallSid);
        }
    }, [fetchAndDispatchSessionSummary, isSimplrSummaryFlagsLoading, shouldAutoGenerateSummary, opened, agentCallSid]);

    useEffect(() => {
        if (hasImmediateCallBack) return;

        if (inWrappingState) {
            openModal();
        } else if (taskIsActive) {
            useSessionSummaryStore.getState().setCanAppend(true);
        }
    }, [hasImmediateCallBack, inWrappingState, taskIsActive, openModal]);

    return {
        opened,
        showCallEndedBanner,
        view,
        summaryHeader: getSummaryHeader(),
        canCallback,
        partner,
        sessionSummaryLoading,
        shouldDisableTextArea,
        shouldShowTextArea,
        canSendSuccessfulSummary,
        buttonText: getSummaryButtonText(),
        summaryUnavailable: sessionSummaryLoading || !summary,
        callbacksRemaining,
        shouldCopy: needsCopy,
        expirySeconds: totalSeconds,
        setView,
        onCopyOrEndSession: handleCopyOrEndSessionClick,
        onScheduleCallback: callbackScheduleHandler,
    };
}
